import React, { useState, useMemo } from "react";
import { Activity, HeartPlus, UserCheck, Trophy, Info } from "lucide-react";
import { getTextEmotionColor, getEmojiImage } from "../utils";
import moment from "moment";

export function StudentTrends({
  state,
  emotionPercents,
  tally,
  compareTally,
  filterName,
}) {
  // For Student Dashboard (single student), participation is 100% if any practice in timeframe, else 0%
  const percent = useMemo(() => (tally > 0 ? 100 : 0), [tally, filterName]);

  const sorted = [...emotionPercents].sort((a, b) => b.change - a.change);
  const mostIncreasedEmotion = sorted[0] || { name: "tired", change: 0 };
  const mostDecreasedEmotion = sorted[sorted.length - 1] || {
    name: "stressed",
    change: 0,
  };

  return (
    <div className="flex-1 bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-5 h-5" />
        <h3 className="text-xl sm:text-2xl font-bold">Trends</h3>
      </div>
      <div className="flex sm:flex-row flex-col items-start sm:items-center justify-between gap-2 w-full">
        <div className="flex flex-col items-center justify-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div className="flex flex-col items-center w-full sm:w-auto gap-1">
            <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
              <div className="flex flex-row items-center gap-2">
                <UserCheck className="w-5 h-5" />
                <div className="text-[16px] text-600 sm:hidden opacity-50">
                  PARTICIPATION
                </div>
              </div>

              <div className="sm:text-[36px] text-[22px] text-700 font-bold">
                {percent}%
              </div>
            </div>
            <div className="text-[16px] text-600 sm:block hidden opacity-50">
              PARTICIPATION
            </div>
            <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
              <div className="flex-1 sm:flex-none sm:w-40 h-2.5 bg-gray-200 rounded-full cursor-pointer">
                <div
                  className="h-2.5 bg-[#FDBA74] rounded-full transition-all duration-300"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <Trophy className="w-4 h-4 text-[#FDBA74]" />
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start">
            <div className="flex flex-row items-center gap-2">
              <HeartPlus className="w-5 h-5" />
              <div className="text-[16px] text-600 text-center sm:hidden opacity-50">
                WELLNESS PRACTICES
              </div>
            </div>
            <div className="sm:text-[36px] text-[22px] text-700 font-bold">
              {tally}
            </div>
          </div>
          <div className="text-[16px] text-600 text-center sm:block hidden opacity-50 mt-1">
            WELLNESS PRACTICES
          </div>
        </div>
        <div className="flex flex-col items-center justify-center w-full sm:w-auto border-b border-[#DDDDDD] py-2 sm:border-none">
          <div
            className={`flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start`}
          >
            <div className="flex flex-row items-center gap-2">
              <img
                src={getEmojiImage(mostIncreasedEmotion.name)}
                alt={mostIncreasedEmotion.name}
                className="h-8 w-8"
              />
              <div className="text-[16px] text-600 text-center sm:hidden opacity-50">
                {mostIncreasedEmotion.name?.toUpperCase()} LEVEL
              </div>
            </div>
            <div className="flex flex-row items-center">
              <div
                className={`mr-1 ${getTextEmotionColor(
                  mostIncreasedEmotion.name
                )}`}
              >
                {mostIncreasedEmotion.change !== 0 && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 10l7-7 7 7M12 3v18"
                    />
                  </svg>
                )}
              </div>
              <div
                className={`text-[18px] text-700 font-bold ${getTextEmotionColor(
                  mostIncreasedEmotion.name
                )}`}
              >
                {Math.abs(mostIncreasedEmotion.change)}%
              </div>
            </div>
          </div>
          <div
            className={`text-[16px] text-600 text-center sm:block hidden mt-1 opacity-50`}
          >
            {mostIncreasedEmotion.name?.toUpperCase()} LEVEL
          </div>
        </div>
        <div className="flex flex-col items-center justify-center sm:mr-4 w-full sm:w-auto py-2">
          <div
            className={`flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start`}
          >
            <div className="flex flex-row items-center gap-2">
              <img
                src={getEmojiImage(mostDecreasedEmotion.name)}
                alt={mostDecreasedEmotion.name}
                className="h-8 w-8"
              />
              <div className="text-[16px] text-600 sm:hidden opacity-50">
                {mostDecreasedEmotion.name?.toUpperCase()} LEVEL
              </div>
            </div>
            <div className="flex flex-row items-center">
              <div
                className={`mr-1 ${getTextEmotionColor(
                  mostDecreasedEmotion.name
                )}`}
              >
                {mostDecreasedEmotion.change !== 0 && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 14l-7 7-7-7M12 3v18"
                    />
                  </svg>
                )}
              </div>
              <div
                className={`text-[18px] text-700 font-bold ${getTextEmotionColor(
                  mostDecreasedEmotion.name
                )}`}
              >
                {Math.abs(mostDecreasedEmotion.change)}%
              </div>
            </div>
          </div>
          <div
            className={`text-[16px] text-600 text-center sm:block hidden mt-1 opacity-50`}
          >
            {mostDecreasedEmotion.name?.toUpperCase()} LEVEL
          </div>
        </div>
      </div>
    </div>
  );
}
